/**
 * TaskAgentsView — task details page: task info + ALL registered agents with
 * their EFFECTIVE profit tiers. Every agent inherits the task tiers by default;
 * the admin can override percentages for individual agents (saved per agent).
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  ArrowRight, Loader2, RefreshCw, Percent, Users, Save, RotateCcw,
  ClipboardList, BadgeCheck, Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGES = {
  active: { ar: 'فعّالة', en: 'Active', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  draft: { ar: 'مسوّدة', en: 'Draft', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  archived: { ar: 'مؤرشفة', en: 'Archived', cls: 'bg-white/5 text-white/40 border-white/10' },
};

export default function TaskAgentsView({ taskId, language = 'ar', onBack }) {
  const isAr = language === 'ar';
  const [task, setTask] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/agent-tasks/${taskId}/agents`, { headers: getAuthHeaders() });
      setTask(res.data?.task || null);
      setAgents((res.data?.agents || []).map((a) => ({
        ...a,
        tiers: (a.tiers || []).map((t) => ({ ...t, percentage: String(t.percentage ?? '') })),
        dirty: false,
      })));
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل تحميل بيانات المهمة' : 'Failed to load task'));
    } finally {
      setLoading(false);
    }
  }, [taskId, isAr]);

  useEffect(() => { load(); }, [load]);

  const defaultTiers = task?.profit_tiers || [];

  const updateAgentTier = (agentId, tierIdx, value) => {
    setAgents((list) => list.map((a) => (a.id === agentId
      ? { ...a, dirty: true, tiers: a.tiers.map((t, i) => (i === tierIdx ? { ...t, percentage: value } : t)) }
      : a)));
  };

  const saveAgent = async (agent) => {
    const tiers = agent.tiers.map((t, i) => ({
      percentage: Number(t.percentage) || 0,
      description_ar: t.description_ar || defaultTiers[i]?.description_ar || '',
      description_en: t.description_en || defaultTiers[i]?.description_en || '',
    }));
    setSavingId(agent.id);
    try {
      await axios.put(
        `${API_URL}/api/admin/agent-tasks/${taskId}/agents/${agent.id}`,
        { tiers },
        { headers: getAuthHeaders() },
      );
      toast.success(isAr ? `✓ تم حفظ نسب ${agent.name || 'الوكيل'} الخاصة` : '✓ Custom rates saved');
      setAgents((list) => list.map((a) => (a.id === agent.id ? { ...a, dirty: false, has_override: true } : a)));
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الحفظ' : 'Save failed'));
    } finally {
      setSavingId(null);
    }
  };

  const resetAgent = async (agent) => {
    setSavingId(agent.id);
    try {
      const res = await axios.delete(
        `${API_URL}/api/admin/agent-tasks/${taskId}/agents/${agent.id}`,
        { headers: getAuthHeaders() },
      );
      const tiers = (res.data?.tiers || []).map((t) => ({ ...t, percentage: String(t.percentage ?? '') }));
      setAgents((list) => list.map((a) => (a.id === agent.id ? { ...a, tiers, dirty: false, has_override: false } : a)));
      toast.success(isAr ? '✓ رجع الوكيل للنسب الافتراضية' : '✓ Agent reset to task defaults');
    } catch (err) {
      toast.error(isAr ? 'فشل إعادة التعيين' : 'Reset failed');
    } finally {
      setSavingId(null);
    }
  };

  const badge = STATUS_BADGES[task?.status] || STATUS_BADGES.active;

  return (
    <div data-testid="task-agents-view">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <button
          onClick={onBack}
          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white"
          data-testid="task-agents-back"
        >
          <ArrowRight className={`h-4 w-4 ${isAr ? '' : 'rotate-180'}`} />
        </button>
        <ClipboardList className="h-6 w-6 text-orange-400" />
        <h2 className="text-xl font-bold text-white flex-1 min-w-0 truncate">
          {loading ? (isAr ? 'جاري التحميل…' : 'Loading…') : (isAr ? task?.title_ar : task?.title_en || task?.title_ar)}
        </h2>
        {task && (
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${badge.cls}`}>
            {isAr ? badge.ar : badge.en}
          </span>
        )}
        <button
          onClick={load}
          disabled={loading}
          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10 flex items-center gap-2"
          data-testid="task-agents-refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-24 bg-white/[0.02] rounded-2xl animate-pulse" />)}
        </div>
      ) : !task ? null : (
        <>
          {/* Task info */}
          <div className="p-5 bg-white/[0.02] rounded-2xl border border-white/10 mb-5" data-testid="task-agents-info">
            {(task.description_ar || task.description_en) && (
              <p className="text-sm text-white/75 mb-3 whitespace-pre-wrap">
                {isAr ? task.description_ar || task.description_en : task.description_en || task.description_ar}
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-white/45 font-bold">{isAr ? 'الشرائح الافتراضية:' : 'Default tiers:'}</span>
              {defaultTiers.length === 0 && (
                <span className="text-[11px] text-amber-300">{isAr ? 'لا توجد شرائح محددة' : 'No tiers defined'}</span>
              )}
              {defaultTiers.map((t, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-300 font-bold">
                  <Percent className="h-3 w-3" />
                  {t.percentage}%
                  {(t.description_ar || t.description_en) && (
                    <span className="text-white/60 font-normal">
                      — {isAr ? t.description_ar || t.description_en : t.description_en || t.description_ar}
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>

          {/* Agents */}
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-orange-400" />
            <h3 className="text-sm font-bold text-white">
              {isAr ? 'الوكلاء المسجلون ونسبهم في هذه المهمة' : 'Registered agents & their rates for this task'}
              <span className="ms-2 text-white/40 font-normal">({agents.length})</span>
            </h3>
          </div>

          {agents.length === 0 ? (
            <div className="text-center py-14 bg-white/[0.02] rounded-2xl border border-white/10 text-white/50">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
              {isAr ? 'لا يوجد وكلاء مسجلون بعد' : 'No registered agents yet'}
            </div>
          ) : (
            <div className="space-y-3">
              {agents.map((agent) => (
                <div
                  key={agent.id}
                  className={`p-4 rounded-2xl border bg-white/[0.02] transition-colors ${agent.has_override ? 'border-amber-500/40' : 'border-white/10'}`}
                  data-testid={`task-agent-row-${agent.id}`}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-bold truncate">{agent.name || agent.email || agent.id}</p>
                        {agent.agent_blocked && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/40 text-red-300">
                            <Ban className="h-3 w-3" />{isAr ? 'محظور' : 'Blocked'}
                          </span>
                        )}
                        {agent.has_override && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 font-bold" data-testid={`task-agent-custom-${agent.id}`}>
                            <BadgeCheck className="h-3 w-3" />{isAr ? 'نسب مخصصة' : 'Custom rates'}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-white/40 truncate" dir="ltr">{agent.email}{agent.phone ? ` · ${agent.phone}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {agent.has_override && (
                        <button
                          onClick={() => resetAgent(agent)}
                          disabled={savingId === agent.id}
                          className="px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/60 text-xs hover:bg-white/10 flex items-center gap-1 disabled:opacity-40"
                          title={isAr ? 'إرجاع للنسب الافتراضية' : 'Reset to defaults'}
                          data-testid={`task-agent-reset-${agent.id}`}
                        >
                          <RotateCcw className="h-3 w-3" />
                          {isAr ? 'افتراضي' : 'Default'}
                        </button>
                      )}
                      <button
                        onClick={() => saveAgent(agent)}
                        disabled={!agent.dirty || savingId === agent.id}
                        className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`task-agent-save-${agent.id}`}
                      >
                        {savingId === agent.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                        {isAr ? 'حفظ' : 'Save'}
                      </button>
                    </div>
                  </div>

                  {/* Tier inputs */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {agent.tiers.map((tier, i) => {
                      const def = defaultTiers[i];
                      const differs = def != null && Number(tier.percentage) !== Number(def.percentage);
                      return (
                        <div
                          key={i}
                          className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${differs ? 'bg-amber-500/[0.06] border-amber-500/40' : 'bg-[#050505] border-white/10'}`}
                        >
                          <span className="text-[10px] text-white/50 flex-1 min-w-0 truncate">
                            {isAr
                              ? (tier.description_ar || def?.description_ar || `شريحة ${i + 1}`)
                              : (tier.description_en || def?.description_en || `Tier ${i + 1}`)}
                          </span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={tier.percentage}
                            onChange={(e) => updateAgentTier(agent.id, i, e.target.value)}
                            className={`w-16 px-2 py-1 bg-black/40 border rounded-lg text-sm text-center focus:outline-none ${differs ? 'border-amber-500/50 text-amber-300 font-bold' : 'border-white/10 text-white'}`}
                            data-testid={`task-agent-tier-${agent.id}-${i}`}
                          />
                          <span className="text-white/50 text-xs">%</span>
                          {differs && def != null && (
                            <span className="text-[9px] text-white/35 whitespace-nowrap" dir="ltr">({def.percentage}%)</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
